import { LayoutGrid } from "lucide-react";
import * as React from "react";
import {
  ApprovalPanel,
  BudgetOrbit,
  CertaintyBadge,
  CostPreview,
  DigestChip,
  EmptyState,
  EvidenceChecklist,
  PageHeader,
  RunTimeline,
  SpendMeter,
  StatusBadge,
  type DomainStatus,
} from "@/components/bespoke";
import {
  ConflictState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NotConnectedState,
  PendingState,
  UnknownOutcome,
} from "@/components/states";
import { ThemeToggle } from "@/components/theme-toggle";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Command, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Toaster } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/components/ui/toast";

function CatalogueItem({
  name,
  description,
  children,
}: {
  name: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card data-catalogue-item={name}>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">{name}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">{children}</CardContent>
    </Card>
  );
}

function CatalogueSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section aria-label={title} data-catalogue-section={title} className="flex flex-col gap-4">
      <div>
        <h2 className="text-[0.9375rem] font-semibold tracking-tight">{title}</h2>
        <p className="mt-1 text-[0.8125rem] text-muted-foreground">{description}</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </section>
  );
}

const runStatuses: DomainStatus[] = [
  "pending",
  "running",
  "waiting",
  "succeeded",
  "failed",
  "cancelled",
  "unknown",
];

const tableRows = [
  { id: "run-1", name: "Refund idempotency", state: "waiting" },
  { id: "run-2", name: "Webhook retries", state: "running" },
  { id: "run-3", name: "Nightly reconciliation", state: "succeeded" },
];

export function ComponentsPage() {
  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Components"
        description="Every component and state in one place, in light and dark. Use the appearance control to compare themes."
        actions={<ThemeToggle />}
      />

      <CatalogueSection
        title="Primitives"
        description="Restyled shadcn/ui building blocks. A disabled control is a flat muted fill, never faded."
      >
        <CatalogueItem
          name="Button"
          description="Default is lime. Destructive is an outline. Disabled never fades."
        >
          <Button>Save</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Delete</Button>
          <Button loading>Working</Button>
          <Button disabled>Unavailable</Button>
          <Button size="sm">Small</Button>
          <Button size="lg">Large</Button>
        </CatalogueItem>

        <CatalogueItem
          name="Badge"
          description="A status is always a labelled pill with a dot. Colour never carries the meaning alone."
        >
          <Badge variant="success">Succeeded</Badge>
          <Badge variant="warning">Waiting</Badge>
          <Badge variant="info">Running</Badge>
          <Badge variant="danger">Failed</Badge>
          <Badge variant="muted">Draft</Badge>
          <Badge variant="unknown">Unknown</Badge>
        </CatalogueItem>

        <CatalogueItem
          name="Card"
          description="16 px radius, 20 px padding, header and footer slots."
        >
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Card title</CardTitle>
              <CardDescription>Card description</CardDescription>
            </CardHeader>
            <CardContent>Card body</CardContent>
          </Card>
        </CatalogueItem>

        <CatalogueItem
          name="Dialog"
          description="20 px radius, plain-language title, consequence, primary action on the right."
        >
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="secondary">Open dialog</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogTitle>Start this run</DialogTitle>
              <DialogDescription>This reserves 400 credits.</DialogDescription>
            </DialogContent>
          </Dialog>
        </CatalogueItem>

        <CatalogueItem
          name="Input"
          description="36 px height, hover border, helper and error slots."
        >
          <div className="flex w-full flex-col gap-2">
            <Input placeholder="Project name" aria-label="Project name" />
            <Input placeholder="Cap in credits" aria-label="Cap in credits" aria-invalid="true" />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="Select"
          description="Form select bound to the same 36 px control height."
        >
          <Select defaultValue="comfortable">
            <SelectTrigger aria-label="Table density">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="comfortable">Comfortable</SelectItem>
              <SelectItem value="compact">Compact</SelectItem>
            </SelectContent>
          </Select>
        </CatalogueItem>

        <CatalogueItem
          name="Table"
          description="Sticky header, density toggle, mono cells for identifiers."
        >
          <div className="w-full">
            <DataTable
              caption="Catalogue table sample"
              columns={[
                { key: "name", header: "Run" },
                { key: "state", header: "State" },
              ]}
              rows={tableRows}
              getRowId={(row) => row.id}
              pageSize={10}
            />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="Tabs"
          description="Underline tabs for pages, segmented control for modes."
        >
          <Tabs defaultValue="runs" className="w-full">
            <TabsList>
              <TabsTrigger value="runs">Runs</TabsTrigger>
              <TabsTrigger value="approvals">Approvals</TabsTrigger>
            </TabsList>
            <TabsContent value="runs">Runs list</TabsContent>
            <TabsContent value="approvals">Approvals list</TabsContent>
          </Tabs>
        </CatalogueItem>

        <CatalogueItem
          name="Textarea"
          description="Multi-line input with the same control treatment."
        >
          <Textarea placeholder="Why is this needed?" aria-label="Reason" />
        </CatalogueItem>

        <CatalogueItem name="Tooltip" description="800 ms delay on hover, instant on focus.">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="secondary">Hover for hint</Button>
            </TooltipTrigger>
            <TooltipContent>Reserves credits before the run starts.</TooltipContent>
          </Tooltip>
        </CatalogueItem>

        <CatalogueItem name="Alert" description="Announced notice with a title and one action.">
          <div className="flex w-full flex-col gap-2">
            <Alert variant="info">
              <AlertTitle>Trial ends soon</AlertTitle>
              <AlertDescription>Add a plan to keep running tasks.</AlertDescription>
            </Alert>
            <Alert variant="warning">
              <AlertTitle>Cap nearly reached</AlertTitle>
              <AlertDescription>Raise the cap to continue.</AlertDescription>
            </Alert>
            <Alert variant="destructive">
              <AlertTitle>Run failed</AlertTitle>
              <AlertDescription>Retry from the last good step.</AlertDescription>
            </Alert>
          </div>
        </CatalogueItem>

        <CatalogueItem name="Toast" description="Sonner toast with an Undo action.">
          <Button
            variant="secondary"
            onClick={() =>
              toast("Approved. The run will continue.", {
                action: { label: "Undo", onClick: () => undefined },
              })
            }
          >
            Show toast
          </Button>
          <Toaster />
        </CatalogueItem>

        <CatalogueItem name="Command" description="The command palette, rendered inline.">
          <Command className="w-full">
            <CommandInput placeholder="Type a command or search" aria-label="Search commands" />
            <CommandList>
              <CommandItem>New run</CommandItem>
              <CommandItem>Go to approvals</CommandItem>
              <CommandItem>Open runs</CommandItem>
            </CommandList>
          </Command>
        </CatalogueItem>

        <CatalogueItem name="Sheet" description="Drawer for details on small screens.">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="secondary">Open details</Button>
            </SheetTrigger>
            <SheetContent>
              <SheetTitle>Run details</SheetTitle>
            </SheetContent>
          </Sheet>
        </CatalogueItem>

        <CatalogueItem name="Dropdown menu" description="Account and overflow menus.">
          <DropdownMenu>
            <DropdownMenuTrigger className={buttonVariants({ variant: "secondary" })}>
              Account
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Settings</DropdownMenuItem>
              <DropdownMenuItem>Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CatalogueItem>

        <CatalogueItem name="Switch" description="Binary choice with a visible label.">
          <label className="flex items-center gap-2 text-sm">
            Pause at cap <Switch defaultChecked aria-label="Pause at cap" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            Compact tables <Switch aria-label="Compact tables" />
          </label>
        </CatalogueItem>

        <CatalogueItem name="Progress" description="Determinate bar with an accessible name.">
          <div className="w-full">
            <Progress aria-label="Monthly spend" value={62} />
          </div>
        </CatalogueItem>

        <CatalogueItem name="Skeleton" description="Holds the layout still while content loads.">
          <Skeleton className="h-4 w-36" />
        </CatalogueItem>

        <CatalogueItem name="Breadcrumb" description="Where this page sits.">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>Project</BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbPage>Runs</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </CatalogueItem>

        <CatalogueItem name="Avatar" description="Initials when no portrait is set.">
          <Avatar>
            <AvatarFallback>MR</AvatarFallback>
          </Avatar>
        </CatalogueItem>

        <CatalogueItem name="Popover" description="Anchored panel for extra detail.">
          <Popover>
            <PopoverTrigger className={buttonVariants({ variant: "secondary" })}>
              More information
            </PopoverTrigger>
            <PopoverContent>Project information</PopoverContent>
          </Popover>
        </CatalogueItem>
      </CatalogueSection>

      <CatalogueSection
        title="Bespoke"
        description="Zworks-specific components. Domain states never read as success before confirmation."
      >
        <CatalogueItem
          name="PageHeader"
          description="One canonical heading per page, with badge, description, and actions."
        >
          <PageHeader
            headingLevel="h3"
            title="Sample page"
            badge={<Badge variant="info">Beta</Badge>}
            description="What this page is for."
            actions={<Button size="sm">Primary action</Button>}
          />
        </CatalogueItem>

        <CatalogueItem
          name="StatusBadge"
          description="Maps run, approval, evidence, and audit states to labelled badges."
        >
          {runStatuses.map((status) => (
            <StatusBadge key={status} kind="run" status={status} />
          ))}
          <StatusBadge kind="approval" status="approved" />
          <StatusBadge kind="approval" status="declined" />
          <StatusBadge kind="evidence" status="verified" />
          <StatusBadge kind="evidence" status="missing" />
        </CatalogueItem>

        <CatalogueItem
          name="CertaintyBadge"
          description="Confirmed, reserved, estimated, or unknown. Unknown is never zero."
        >
          <CertaintyBadge certainty="confirmed" />
          <CertaintyBadge certainty="reserved" />
          <CertaintyBadge certainty="estimated" />
          <CertaintyBadge certainty="unknown" />
        </CatalogueItem>

        <CatalogueItem
          name="SpendMeter"
          description="Used and reserved against the limit, dollars beside credits."
        >
          <div className="w-full">
            <SpendMeter used={1200} reserved={400} limit={2000} />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="BudgetOrbit"
          description="Used, projected, and reserved rings against the monthly limit."
        >
          <BudgetOrbit used={1200} projected={400} reserved={200} limit={2000} />
        </CatalogueItem>

        <CatalogueItem
          name="DigestChip"
          description="Mono identifier with truncation and one-click copy."
        >
          <DigestChip value="sha256:9f2c4a1e77b03dd5c6a8e10f4b2c9d7a1e5f3b8c0d2a4e6f8b1c3d5e7a9b0c1d" />
        </CatalogueItem>

        <CatalogueItem
          name="CostPreview"
          description="Estimate range, cap marker, and balance after the run."
        >
          <div className="w-full">
            <CostPreview estimateMin={280} estimateMax={400} cap={400} currentBalance={1600} />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="ApprovalPanel"
          description="Decision gate. Authors see context but never their own buttons."
        >
          <div className="w-full">
            <ApprovalPanel
              title="Deploy to staging"
              actionDigest="sha256:9f2c"
              revision={3}
              whatChanged={<p>One file changed in the deploy plan.</p>}
              isAuthor={true}
              requesterName="M. Reyes"
            />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="EvidenceChecklist"
          description="Files, tests, approvals, secrets, and network with signature status."
        >
          <div className="w-full">
            <EvidenceChecklist
              signatureStatus="verified"
              items={[
                { id: "files", name: "Files", detail: "12 files signed", status: "verified" },
                { id: "tests", name: "Tests", detail: "48 passed", status: "verified" },
                {
                  id: "approvals",
                  name: "Approvals",
                  detail: "Waiting on one",
                  status: "unverified",
                },
                {
                  id: "secrets",
                  name: "Secrets",
                  detail: "Not scanned yet",
                  status: "not-yet-due",
                },
              ]}
            />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="RunTimeline"
          description="Plan, edit, install, test, and review with durations."
        >
          <div className="w-full">
            <RunTimeline />
          </div>
        </CatalogueItem>

        <CatalogueItem name="EmptyState" description="Names what is missing and offers one action.">
          <div className="w-full">
            <EmptyState
              icon={LayoutGrid}
              title="No runs yet"
              description="Start the first run to see it here."
              action={{ label: "Start a run" }}
            />
          </div>
        </CatalogueItem>
      </CatalogueSection>

      <CatalogueSection
        title="States"
        description="Loading, empty, failure, and pending treatments shared by every page."
      >
        <CatalogueItem name="LoadingState" description="Skeleton rows that hold the layout.">
          <div className="w-full">
            <LoadingState label="Loading runs" rows={2} />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="NotConnectedState"
          description="Names the missing capability and offers one action."
        >
          <div className="w-full">
            <NotConnectedState
              title="Assistant is not connected yet"
              description="Connect an assistant adapter to ask questions."
            />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="ErrorState"
          description="What happened, what to do, and the reference id."
        >
          <div className="w-full">
            <ErrorState
              title="Runs did not load"
              description="Check the connection and try again."
              requestId="req-42"
              onRetry={() => undefined}
            />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="PendingState"
          description="Accepted but not finished. A 202 never reads as success."
        >
          <div className="w-full">
            <PendingState
              title="Run accepted"
              description="The run is queued. The badge stays pending until it finishes."
              badge="Pending"
              action="Cancel run"
            />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="ConflictState"
          description="Reload first when trusted state moved. Deciding waits."
        >
          <div className="w-full">
            <ConflictState
              title="This approval changed"
              description="Someone updated it while you were reading."
              onReload={() => undefined}
            />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="ForbiddenState"
          description="A 403 clears the content first and explains second."
        >
          <div className="w-full">
            <ForbiddenState
              title="Only owners buy credits"
              description="Ask an owner to top up this project."
            />
          </div>
        </CatalogueItem>

        <CatalogueItem
          name="UnknownOutcome"
          description="An unknown effect stays visible until it is reconciled."
        >
          <div className="w-full">
            <UnknownOutcome
              title="One charge is unreconciled"
              description="It stays here until the ledger confirms it."
              amount="312 credits"
            />
          </div>
        </CatalogueItem>
      </CatalogueSection>
    </div>
  );
}
